/**
 * Business Central API Client
 *
 * Provides methods for calling Business Central APIs (Payroll, Payslip, etc.)
 * Separate from Dynamics 365 API with its own credentials
 */

// Debug: Log environment variables on module load
console.log("🔧 BC Client Loading...");
console.log(
  "   REACT_APP_BC_TOKEN_API_URL:",
  process.env.REACT_APP_BC_TOKEN_API_URL
);
console.log(
  "   REACT_APP_BC_API_BASE_URL:",
  process.env.REACT_APP_BC_API_BASE_URL
);

/**
 * Fetch OAuth access token for Business Central API
 * @returns {Promise<Object>} Token data with access_token, expires_in, etc.
 */
export async function getBCAccessToken() {
  try {
    const tokenUrl = process.env.REACT_APP_BC_TOKEN_API_URL;
    console.log("🔑 Fetching BC token from:", tokenUrl);
    const response = await fetch(tokenUrl);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message || "Failed to get Business Central access token"
      );
    }

    const tokenData = await response.json();
    console.log("✅ Business Central access token retrieved");
    return tokenData;
  } catch (error) {
    console.error("❌ Error getting Business Central access token:", error);
    throw error;
  }
}

/**
 * Make a GET request to Business Central API via backend proxy
 * @param {string} endpoint - API endpoint path
 * @param {string} token - OAuth access token
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Object>} API response data
 */
export async function bcGet(endpoint, token, options = {}) {
  return bcRequest("GET", endpoint, token, null, options);
}

/**
 * Make a POST request to Business Central API via backend proxy
 * @param {string} endpoint - API endpoint path
 * @param {string} token - OAuth access token
 * @param {Object} data - Request body data
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Object>} API response data
 */
export async function bcPost(endpoint, token, data, options = {}) {
  return bcRequest("POST", endpoint, token, data, options);
}

/**
 * Make a PUT request to Business Central API via backend proxy
 * @param {string} endpoint - API endpoint path
 * @param {string} token - OAuth access token
 * @param {Object} data - Request body data
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Object>} API response data
 */
export async function bcPut(endpoint, token, data, options = {}) {
  return bcRequest("PUT", endpoint, token, data, options);
}

/**
 * Make a PATCH request to Business Central API via backend proxy
 * @param {string} endpoint - API endpoint path
 * @param {string} token - OAuth access token
 * @param {Object} data - Request body data
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Object>} API response data
 */
export async function bcPatch(endpoint, token, data, options = {}) {
  return bcRequest("PATCH", endpoint, token, data, options);
}

/**
 * Make a DELETE request to Business Central API via backend proxy
 * @param {string} endpoint - API endpoint path
 * @param {string} token - OAuth access token
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Object>} API response data
 */
export async function bcDelete(endpoint, token, options = {}) {
  return bcRequest("DELETE", endpoint, token, null, options);
}

/**
 * Generic function to make HTTP requests to Business Central API
 * @param {string} method - HTTP method
 * @param {string} endpoint - API endpoint path
 * @param {string} token - OAuth access token
 * @param {Object} data - Request body (for POST/PUT/PATCH)
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Object>} API response data
 */
async function bcRequest(method, endpoint, token, data = null, options = {}) {
  try {
    const bcBaseUrl = process.env.REACT_APP_BC_API_BASE_URL;

    console.log("========================================");
    console.log("🔍 BC REQUEST DEBUG INFO");
    console.log("========================================");
    console.log("ENV VAR:", process.env.REACT_APP_BC_API_BASE_URL);
    console.log("Base URL:", bcBaseUrl);
    console.log("Original Endpoint:", endpoint);

    // Ensure endpoint starts with /
    const normalizedEndpoint = endpoint.startsWith("/")
      ? endpoint
      : `/${endpoint}`;
    const url = `${bcBaseUrl}${normalizedEndpoint}`;

    console.log(`📤 BC ${method} Request`);
    console.log(`   Base URL: ${bcBaseUrl}`);
    console.log(`   Endpoint: ${normalizedEndpoint}`);
    console.log(`   Full URL: ${url}`);
    console.log("========================================");

    const fetchOptions = {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
        ...options.headers, // Custom headers merge AFTER defaults, so they override if needed
      },
      ...options,
    };

    console.log('📋 Request headers:', fetchOptions.headers);

    // Add body for POST/PUT/PATCH requests
    if (data && ["POST", "PUT", "PATCH"].includes(method)) {
      fetchOptions.body = JSON.stringify(data);
    }

    const response = await fetch(url, fetchOptions);

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: errorText };
      }

      console.error(
        `❌ BC ${method} ${normalizedEndpoint} failed:`,
        response.status,
        errorData
      );

      // Extract error message from Business Central error structure
      let errorMessage = `Request failed with status ${response.status}`;
      if (errorData.error && errorData.error.message) {
        // Business Central error format: { error: { code: "...", message: "..." } }
        errorMessage = errorData.error.message;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }

      throw new Error(errorMessage);
    }

    // Handle empty responses (e.g., 204 No Content)
    if (
      response.status === 204 ||
      response.headers.get("content-length") === "0"
    ) {
      console.log(`✅ BC ${method} ${normalizedEndpoint} - No content`);
      return null;
    }

    const responseData = await response.json();
    console.log(`✅ BC ${method} ${normalizedEndpoint} - Success`);
    return responseData;
  } catch (error) {
    console.error(`❌ Error in BC ${method} ${endpoint}:`, error);
    throw error;
  }
}

/**
 * Business Central API client object
 */
export const bcClient = {
  // Token management
  getToken: getBCAccessToken,

  // HTTP methods
  get: bcGet,
  post: bcPost,
  put: bcPut,
  patch: bcPatch,
  delete: bcDelete,
};

export default bcClient;
